import store from '@/store';
import { dateDiff, nextMonthJP } from '@/utils/year_helpers';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

export function basicPresent(field) {
  return field ? field : '―';
}

export function basicFieldPresent(object, field) {
  if (object && object[field]) {
    return object[field];
  }
  return '―';
}

export function basicSubFieldPresent(object, subObject, field) {
  if (object && object[subObject] && object[subObject][field]) {
    return object[subObject][field];
  }
  return '―';
}

export function basicFrozenStatus(field) {
  if (field) {
    let date = moment(field.frozenDatetime);
    return '凍結予約（' + date.format('YYYY/MM') + '）';
  }
  return '凍結';
}

export function basicBillingStatus(teachingMaterial) {
  if (!isEmpty(teachingMaterial)) {
    return '●';
  }
  return '―';
}

export function basicDatetime(field) {
  if (field) {
    let date = new Date(field);
    return moment(date).format('YYYY/MM/DD HH:mm');
  }
  return '';
}

export function basicDate(field) {
  if (field) {
    let date = new Date(field);
    return moment(date).format('YYYY/MM/DD');
  }
  return '';
}

export function basicJapaneseYearMonth(field) {
  if (field) {
    let date = new Date(field);
    return moment(date).format('YYYY年MM月');
  }
  return '―';
}

export function basicBirthday(yearOfBirth, monthOfBirth) {
  if (yearOfBirth && monthOfBirth) {
    return yearOfBirth + '年' + monthOfBirth + '月';
  }
  return '';
}

export function basicMoneyAmount(amount, showCurrency) {
  if (amount) {
    if (showCurrency) {
      var formatter = new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
      });

      return formatter.format(amount);
    } else {
      return Intl.NumberFormat().format(amount);
    }
  }
  return 0;
}

export function basicCorporationNameIdPresent(corporation) {
  if (corporation && corporation.name && corporation.uid) {
    return basicNameId(corporation.name, corporation.uid);
  }
  return '';
}

export function basicBrandNameIdPresent(brand) {
  if (brand && brand.name && brand.uid) {
    return basicNameId(brand.name, brand.uid);
  }
  return '';
}

export function basicSchoolNameIdPresent(school) {
  if (school && school.name && school.uid) {
    return basicNameId(school.name, school.uid);
  }
  return '';
}

export function basicCourseNameIdPresent(course) {
  if (course && course.name && course.uid) {
    return basicNameId(course.name, course.uid);
  }
  return '';
}

export function basicTamaiClassNameIdPresent(tamaiClass) {
  if (tamaiClass && tamaiClass.name && tamaiClass.uid) {
    return basicNameId(tamaiClass.name, tamaiClass.uid);
  }
  return '';
}

export function basicTeacherNameIdPresent(teacher) {
  if (teacher && teacher.name && teacher.uid) {
    return basicNameId(teacher.name, teacher.uid);
  }
  return '';
}

export function basicStudentNameIdPresent(student) {
  if (student && student.name && student.uid) {
    return basicNameId(student.name, student.uid);
  }
  return '';
}

export function basicTrialStudentNameIdPresent(student) {
  if (student && student.uid) {
    return '体験用（' + student.uid + '）';
  }
  return '―';
}

export function basicProductName(products) {
  if (Array.isArray(products)) {
    const productNames = products.map((product) => product.name);
    return productNames.join('・');
  }
  return '';
}

export function basicFrozenType(type) {
  switch (type) {
    case 'corporation':
      return '法人';
    case 'brand':
      return 'ブランド';
    case 'school':
      return '教場';
    case 'course':
      return 'コース';
    case 'class':
      return 'クラス';
    case 'teacher':
      return '先生';
    case 'student':
      return '生徒';
    case 'video':
      return '映像視聴';
    default:
      return '';
  }
}

const basicNameId = (fieldName, fieldId) => {
  return fieldName + '（' + fieldId + '）';
};

export function basicRecipientDisplay(recipientIds) {
  if (!recipientIds) return '―';

  const recipientOption = find(store.state.notification.recipientOptions, (option) => option.id == recipientIds);
  return recipientOption ? recipientOption.name : '―';
}

export function basicFrozenRemainingDays(date) {
  const differences = dateDiff(new Date(date), new Date());
  const remainingDays = 30 - differences;
  return (remainingDays > 0 ? remainingDays : 0) + '日';
}

export function basicPreRegistrationRemainingDays(date) {
  const differences = dateDiff(new Date(date), new Date());
  const remainingDays = 180 - differences;
  return '残り' + (remainingDays > 0 ? remainingDays : 0) + '日';
}

export function basicDeactivatedRemainingDays(date) {
  const differences = dateDiff(new Date(date), new Date());
  const remainingDays = 180 - differences;
  return '残り' + (remainingDays > 0 ? remainingDays : 0) + '日';
}

export function remainingDaysToDeletePresent(student) {
  if (student.preRegistration && student.preRegistrationDatetime == null) {
    return basicPreRegistrationRemainingDays(student.createdAt);
  } else if (student.deactivated && student.deactivatedDatetime != null) {
    return basicDeactivatedRemainingDays(student.deactivatedDatetime);
  }
  return '―';
}

export function basicBillingTypeDisplay(type) {
  switch (type) {
    case 'corporation':
      return '法人';
    case 'brand':
      return 'ブランド';
    case 'school':
      return '教場';
    default:
      return '―';
  }
}

export function basicBillingTypeIdDisplay(type) {
  switch (type) {
    case 'studentKiwami1':
      return '生徒ID（極 - 1教材）';
    case 'studentKiwami1Unbillable':
      return '生徒ID（極 - 1教材）請求対象外';
    case 'studentKiwami2':
      return '生徒ID（極 - 2教材）';
    case 'studentKiwami2Unbillable':
      return '生徒ID（極 - 2教材）請求対象外';
    case 'studentKokugo':
      return '生徒ID（国語）';
    case 'studentKokugoUnbillable':
      return '生徒ID（国語）請求対象外';
    case 'studentAsobi':
      return '生徒ID（遊）';
    case 'studentAsobiUnbillable':
      return '生徒ID（遊）請求対象外';
    case 'studentSatori':
      return '生徒ID（悟）';
    case 'studentSatoriUnbillable':
      return '生徒ID（悟）請求対象外';
    case 'student1':
      return '生徒ID（1教材）';
    case 'student1Unbillable':
      return '生徒ID（1教材）請求対象外';
    case 'student2':
      return '生徒ID（2教材）';
    case 'student2Unbillable':
      return '生徒ID（2教材）請求対象外';
    case 'trial':
      return '体験用ID';
    case 'teacher':
      return '先生ID';
    case 'teacherUnbillable':
      return '先生ID 請求対象外';
    case 'teacherFreeTrial':
      return '先生ID（初月無料割引）';
    case 'oneSchoolProduct':
      return '教場費用 1教材（映像視聴ID1教材付）';
    case 'oneSchoolProductFree':
      return '教場費用 1教材（請求対象外）';
    case 'twoSchoolProduct':
      return '教場費用 2教材（映像視聴ID2教材付）';
    case 'twoSchoolProductFree':
      return '教場費用 2教材（請求対象外）';
    case 'threeSchoolProduct':
      return '教場費用 3教材（映像視聴ID3教材付）';
    case 'threeSchoolProductFree':
      return '教場費用 3教材（請求対象外）';
    case 'fourSchoolProduct':
      return '教場費用 4教材（映像視聴ID4教材付）';
    case 'fourSchoolProductFree':
      return '教場費用 4教材（請求対象外）';
    case 'videoViewing':
      return '映像視聴ID（教場ID付帯分）';
    case 'additionalVideoViewing':
      return '映像視聴ID（追加発行分）';
    case 'brand':
      return 'ブランドID';
    case 'school':
      return '教場ID';
    default:
      return '―';
  }
}

// 図形の極
export function basicBillingTeachingMaterial400Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial400 = teachingMaterials.find((material) => material.code == '400');
    return basicBillingStatus(teachingMaterial400);
  }
  return '―';
}

// 数の極
export function basicBillingTeachingMaterial200Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial200 = teachingMaterials.find((material) => material.code == '200');
    return basicBillingStatus(teachingMaterial200);
  }
  return '―';
}

// 中学生の図形の極
export function basicBillingTeachingMaterial100Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial100 = teachingMaterials.find((material) => material.code == '100');
    return basicBillingStatus(teachingMaterial100);
  }
  return '―';
}

// 中学生の代数の極
export function basicBillingTeachingMaterial300Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial300 = teachingMaterials.find((material) => material.code == '300');
    return basicBillingStatus(teachingMaterial300);
  }
  return '―';
}

// 国語
export function basicBillingTeachingMaterial700Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial700 = teachingMaterials.filter((material) => material.code == '700');
    return basicBillingStatus(teachingMaterial700);
  }
  return '―';
}

// 遊
export function basicBillingTeachingMaterial600Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial600 = teachingMaterials.find((material) => material.code == '600');
    return basicBillingStatus(teachingMaterial600);
  }
  return '―';
}

// 悟
export function basicBillingTeachingMaterial500Display(teachingMaterials) {
  if (teachingMaterials) {
    const teachingMaterial500 = teachingMaterials.filter((material) => material.code == '500');
    return basicBillingStatus(teachingMaterial500);
  }
  return '―';
}

export function basicLevelDisplay(level) {
  if (level == null || level == 'err-1') {
    return '―';
  }
  return level;
}

export function basicNextTeachingMaterialMonth(nextFiguredTeachingMaterials) {
  if (!isEmpty(nextFiguredTeachingMaterials)) {
    return nextMonthJP();
  }
  return '―';
}

export function basicTeachingMaterialShapeDisplay(figuredTeachingMaterials) {
  if (figuredTeachingMaterials) {
    const materials = figuredTeachingMaterials.filter((material) => material.type == 'shape');
    return basicPresent(materials.map((material) => material.name).join('・'));
  }
  return '―';
}

export function basicTeachingMaterialAlgebraDisplay(figuredTeachingMaterials) {
  if (figuredTeachingMaterials) {
    const materials = figuredTeachingMaterials.filter((material) => material.type == 'algebra');
    return basicPresent(materials.map((material) => material.name).join('・'));
  }
  return '―';
}

export function basicTeachingMaterialJapaneseDisplay(figuredTeachingMaterials) {
  if (figuredTeachingMaterials) {
    const materials = figuredTeachingMaterials.filter((material) => material.type == 'Japanese');
    return basicPresent(materials.map((material) => material.name).join('・'));
  }
  return '―';
}

export function basicTeachingMaterialKidsMathematicsDisplay(figuredTeachingMaterials) {
  if (figuredTeachingMaterials) {
    const materials = figuredTeachingMaterials.filter((material) => material.type == 'kids mathematics');
    return basicPresent(materials.map((material) => material.name).join('・'));
  }
  return '―';
}

export function basicTeachingMaterialAdvancedMathematicsDisplay(figuredTeachingMaterials) {
  if (figuredTeachingMaterials) {
    const materials = figuredTeachingMaterials.filter((material) => material.type == 'advanced mathematics');
    return basicPresent(materials.map((material) => material.name).join('・'));
  }
  return '―';
}

export function preRegistrationStatusDisplay(student) {
  if (student.reactivatedDatetime || student.preRegistrationDatetime) {
    return '利用開始待ち';
  } else if (student.deactivated) {
    return '休会中';
  } else if (student.preRegistration) {
    return '仮登録';
  }
  return '―';
}

export function preRegistrationDateStatusDisplay(student) {
  if (student.reactivatedDatetime) {
    return basicJapaneseYearMonth(student.reactivatedDatetime);
  } else if (student.preRegistrationDatetime) {
    return basicJapaneseYearMonth(student.preRegistrationDatetime);
  }
  return '―';
}
